import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTenantAuth } from '@/auth/TenantAuthContext';
import { Alert } from '@/components/Alert';
import { AuthSplitLayout } from '@/components/AuthSplitLayout';
import { Button } from '@/components/Button';
import { TextField } from '@/components/Form';
import { getErrorMessage } from '@/lib/api';
import { safePortalRedirect } from '@/lib/routes';

interface LocationState {
  from?: string;
}

/** Tenant-admin sign-in. Separate credentials and separate session from /admin. */
export function PortalLoginPage() {
  const { isAuthenticated, login } = useTenantAuth();
  const location = useLocation();
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ emailOrUsername?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const state = location.state as LocationState | null;
  // Only ever return into the tenant portal, never into an /admin route.
  const redirectTo = safePortalRedirect(state?.from);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const errors: { emailOrUsername?: string; password?: string } = {};
      if (!emailOrUsername.trim()) errors.emailOrUsername = 'Enter your email or username.';
      if (!password) errors.password = 'Enter your password.';
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      setFormError(null);
      setSubmitting(true);
      void login({ emailOrUsername: emailOrUsername.trim(), password })
        .catch((error: unknown) => {
          setFormError(getErrorMessage(error, 'Could not sign in. Please try again.'));
        })
        .finally(() => setSubmitting(false));
    },
    [emailOrUsername, login, password],
  );

  if (isAuthenticated) return <Navigate to={redirectTo} replace />;

  return (
    <AuthSplitLayout
      headline="Your shop's numbers, wherever you are."
      blurb="Sales, shifts, delivery and ledger from your till — totalled and ready to read, without standing at the counter."
      points={[
        'Daily sales, taxes and payment mix',
        'Shift and staff performance reports',
        'Receivables, payables and delivery runs',
      ]}
      title="Restaurant reports"
      subtitle="Sign in to view your sales, shifts and ledger."
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {formError ? <Alert tone="error">{formError}</Alert> : null}

        <TextField
          id="portal-emailOrUsername"
          label="Email or username"
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          autoFocus
          value={emailOrUsername}
          onChange={(event) => setEmailOrUsername(event.target.value)}
          error={fieldErrors.emailOrUsername}
          disabled={submitting}
        />

        <TextField
          id="portal-password"
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          disabled={submitting}
        />

        <Button type="submit" variant="primary" className="w-full" loading={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthSplitLayout>
  );
}
